import type { ReactNode } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

/**
 * Generic destructive-confirm dialog — mirrors `apps/admin-web/src/components/ConfirmModal.tsx`
 * (a `CModal` there; MUI `Dialog` here). Used for "Delete permanently" (docs/323 v0 Fix B, the one
 * no-undo action) and — via the additive `children` slot (docs/340 v1 Part C) — the
 * `PersonDetailDrawer` moderation actions' optional reason field. `children` is
 * ALWAYS optional and every pre-existing caller omits it, so this is a purely
 * additive change with no behavior change for them.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  busy,
  error,
  danger = true,
  onCancel,
  onConfirm,
  children,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  busy: boolean;
  error: string | null;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {body ? <DialogContentText>{body}</DialogContentText> : null}
        {children}
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" color={danger ? "error" : "primary"} onClick={onConfirm} disabled={busy}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
